import React, { useState, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes, faTrash } from "@fortawesome/free-solid-svg-icons"
import { TAgetAdmins } from "../../../../services/brandAPI"
import { Admin } from "../../../../types/brandDatas"

interface ResponsibleAdminModalProps {
  isOpen: boolean
  onClose: () => void
  brandId: string
  currentAdmins?: string[]
  onAssign: (brandId: string, adminEmails: string[]) => Promise<void>
}

const ResponsibleAdminModal: React.FC<ResponsibleAdminModalProps> = ({
  isOpen,
  onClose,
  brandId,
  currentAdmins,
  onAssign,
}) => {
  const [admins, setAdmins] = useState<Admin[]>([])
  const [selectedAdmins, setSelectedAdmins] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      fetchAdmins()
      setSelectedAdmins(currentAdmins || [])
    }
  }, [isOpen, currentAdmins])

  const fetchAdmins = async () => {
    try {
      setLoading(true)
      const response = await TAgetAdmins()
      const formattedAdmins = response.map((admin: any) => ({
        _id: admin._id,
        email: admin.email,
        name: admin.name || admin.email,
        password: admin.password || "",
        createdAt: admin.createdAt || new Date().toISOString(),
        updatedAt: admin.updatedAt || new Date().toISOString(),
        __v: admin.__v || 0,
      }))
      setAdmins(formattedAdmins)
      setError(null)
    } catch (error) {
      setError("Admin listesi yüklenirken hata oluştu")
    } finally {
      setLoading(false)
    }
  }

  const handleAssign = async () => {
    if (selectedAdmins.length === 0) {
      setError("Lütfen en az bir admin seçin")
      return
    }

    try {
      setLoading(true)
      await onAssign(brandId, selectedAdmins)
      onClose()
      setError(null)
    } catch (error) {
      setError("Admin atama işlemi başarısız oldu")
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveAdmin = (adminEmail: string) => {
    setSelectedAdmins((prev) => prev.filter((email) => email !== adminEmail))
    setError(null)
  }

  const handleRemoveAll = async () => {
    if (selectedAdmins.length === 0) {
      setError("Kaldırılacak admin yok")
      return
    }

    try {
      setLoading(true)
      await onAssign(brandId, [])
      setSelectedAdmins([])
      setError(null)
      onClose()
    } catch (error) {
      setError("Admin kaldırma işlemi başarısız oldu")
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed right-0 top-0 h-full z-50">
      <div className="bg-white h-full w-96 shadow-lg flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold">Sorumlu Admin Ata</h2>
              <p className="text-sm opacity-90 mt-1">Marka ID: {brandId}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Admin Seçin</label>
            <p className="text-xs text-gray-500 mb-2">
              Birden fazla admin seçmek için Ctrl (Windows) veya Cmd (Mac) tuşunu basılı tutarak
              tıklayın
            </p>
            {loading ? (
              <div className="flex items-center justify-center h-10">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-blue-600"></div>
              </div>
            ) : (
              <select
                multiple
                value={selectedAdmins}
                onChange={(e) => {
                  const selectedOptions = Array.from(
                    e.target.selectedOptions,
                    (option) => option.value,
                  )
                  setSelectedAdmins(selectedOptions)
                }}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-64 max-h-96 overflow-y-auto"
                size={10}
              >
                {admins.map((admin) => (
                  <option key={admin._id} value={admin.email}>
                    {admin.name || admin.email}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="space-y-3">
            <div className="text-sm text-gray-600">
              {selectedAdmins.length > 0 ? (
                <div>
                  <p className="mb-2 font-medium">Seçilen Adminler ({selectedAdmins.length}):</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {selectedAdmins.map((admin, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white px-3 py-1.5 rounded border border-gray-200"
                      >
                        <span className="text-xs">{admin}</span>
                        <button
                          onClick={() => handleRemoveAdmin(admin)}
                          className="text-red-600 hover:text-red-800 p-1"
                          disabled={loading}
                          title="Kaldır"
                        >
                          <FontAwesomeIcon icon={faTrash} className="text-xs" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Admin seçilmedi</p>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={loading}
              >
                İptal
              </button>
              {selectedAdmins.length > 0 && (
                <button
                  onClick={handleRemoveAll}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? "Kaldırılıyor..." : "Tümünü Kaldır"}
                </button>
              )}
              <button
                onClick={handleAssign}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                disabled={loading || selectedAdmins.length === 0}
              >
                {loading ? "Atanıyor..." : "Ata"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResponsibleAdminModal
